import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Component to bypass X-Frame-Options restrictions for iframe embeds
 * @param {Object} props - Component props
 * @param {string} props.src - Source URL for the iframe
 * @param {Object} props - Additional props to pass to the iframe element
 * @returns {JSX.Element} Rendered iframe component
 */
const XFrameBypassIframe = ({ src, ...props }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    // Ensure x-frame-bypass custom element is defined
    if (!customElements.get('x-frame-bypass')) {
      console.log('Iframe bypass not defined, using fallback method');
    }
    
    // Apply a more compatible approach that works with WordPress embedding
    if (iframeRef.current) {
      // Use srcdoc for maximum compatibility
      const html = `
        <html>
          <head>
            <style>
              html, body { margin: 0; padding: 0; height: 100%; width: 100%; overflow: hidden; }
              body { display: flex; flex-direction: column; }
              iframe { flex-grow: 1; border: none; width: 100%; height: 100%; }
            </style>
            <script>
              // Attempt to handle Vercel authentication
              window.addEventListener('message', function(e) {
                if (e.data && e.data.vercelAuthEvent) {
                  window.parent.postMessage(e.data, '*');
                }
              });
            </script>
          </head>
          <body>
            <iframe 
              src="${src}" 
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen
              sandbox="allow-forms allow-modals allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
              style="width: 100%; height: 100%; border: none;"
            ></iframe>
          </body>
        </html>
      `;
      
      iframeRef.current.srcdoc = html;
      
      // Set fallback direct src as backup
      if (!iframeRef.current.srcdoc) {
        iframeRef.current.src = src;
      }
    }
  }, [src]);

  // Use standard iframe with is attribute as fallback, let the custom element definition handle it
  return (
    <iframe 
      is="x-frame-bypass" 
      ref={iframeRef} 
      title={props.title || 'External content'} 
      sandbox="allow-forms allow-modals allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
      style={{ width: '100%', height: '100%', border: 'none' }}
      {...props} 
    />
  );
};

XFrameBypassIframe.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string
};

export default XFrameBypassIframe;